import React, { useEffect, useMemo, useState } from 'react';
import {
  Activity, Filter, Search, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { aiTrainerClient } from '@/lib/aiTrainerClient';
import { useAiTrainerAuth } from '@/contexts/AiTrainerAuthContext';
import { CorpusHero } from './Hero';
import { KPIStrip } from './KPIStrip';
import { PaperCard } from './PaperCard';
import { PendingSheet } from './PendingSheet';
import { DetailDrawer } from './DetailDrawer';
import { PdfPreviewDrawer } from './PdfPreviewDrawer';
import {
  type CorpusStats, type Paper,
  type PaperStatus, type PendingUpload, type Taxonomy,
} from './types';

const PROCESSING: PaperStatus[] = ['queued', 'parsing', 'chunking', 'embedding', 'extracting_outcomes'];

type StatusFilter = 'all' | 'ready' | 'processing' | 'failed';

/**
 * ExamCorpusUpload — examiner-facing surface for training the
 * institution's self-hosted question-generation model.
 *
 * Drop PDFs → tag them in a slide-up sheet → backend ingests
 * (parse → chunk → embed → pgvector) → cards in the library light up.
 * Tap a card to open the detail drawer and trigger question generation
 * or reprocess / remove.
 *
 * Backend contract:
 *   GET  /exam-corpus/papers
 *   GET  /exam-corpus/stats
 *   POST /exam-corpus/papers          multipart: file=PDF, meta=JSON
 *   POST /exam-corpus/papers/:id/reprocess
 *   POST /exam-corpus/papers/:id/delete
 */
interface ExamCorpusUploadProps {
  /** When set, this surface is scoped to a single upload kind:
   *  - the papers list is filtered to that kind,
   *  - all newly-queued pending items are pre-tagged with it,
   *  - the kind toggle in the pending sheet is hidden so the trainer
   *    can't accidentally cross-post a curriculum PDF into the past-
   *    paper corpus (or vice-versa).
   *  Omit for the default `/library` surface, which mixes both. */
  kindFilter?: 'exam_paper' | 'curriculum';
  /** Optional visual overrides for scoped surfaces (e.g. Curriculum
   *  page uses a different eyebrow + tagline in the hero). */
  heroEyebrow?: string;
  heroTitle?: React.ReactNode;
  heroSub?: string;
}

const ExamCorpusUpload: React.FC<ExamCorpusUploadProps> = ({
  kindFilter,
  heroEyebrow,
  heroTitle,
  heroSub,
}) => {
  const { user } = useAiTrainerAuth();

  const [papers, setPapers] = useState<Paper[]>([]);
  const [stats, setStats] = useState<CorpusStats>({
    total_papers: 0, ready: 0, processing: 0, failed: 0,
    total_chunks: 0, total_questions: 0,
  });
  // Deduplicated curriculum tree pulled from server_api (through the
  // ai_training taxonomy proxy). Cached once per session — the tree
  // changes rarely and the picker cascades entirely client-side.
  const [taxonomy, setTaxonomy] = useState<Taxonomy | null>(null);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [detail, setDetail]     = useState<Paper | null>(null);
  const [previewOf, setPreview] = useState<Paper | null>(null);

  /* ── Fetchers ───────────────────────────────────────────────── */
  const fetchPapers = async () => {
    setLoading(true);
    try {
      const r = await aiTrainerClient.get('/papers', {
        params: kindFilter ? { kind: kindFilter } : undefined,
      });
      if (r.data?.success) setPapers(r.data.data?.papers || []);
    } catch {
      setPapers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const r = await aiTrainerClient.get('/stats');
      if (r.data?.success) setStats(r.data.data);
    } catch { /* keep zeros */ }
  };

  // One-shot taxonomy fetch — the tree is small (names + codes only)
  // and lets the picker cascade without further round-trips.
  const fetchTaxonomy = async () => {
    setTaxonomyLoading(true);
    try {
      const r = await aiTrainerClient.get('/taxonomy');
      if (r.data?.success) setTaxonomy(r.data.data);
    } catch {
      // Leave taxonomy null — the picker renders a helpful diagnostic.
    } finally {
      setTaxonomyLoading(false);
    }
  };

  useEffect(() => {
    fetchPapers();
    fetchStats();
    fetchTaxonomy();
  }, [user?.id]);

  // Poll every 6 s while anything is processing.
  useEffect(() => {
    const hasProcessing = papers.some((p) => PROCESSING.includes(p.status));
    if (!hasProcessing) return;
    const t = window.setInterval(() => { fetchPapers(); fetchStats(); }, 6000);
    return () => window.clearInterval(t);
  }, [papers]);

  /* ── Pending uploads ────────────────────────────────────────── */
  const newId = () => `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  const acceptFiles = (files: FileList | File[]) => {
    const next: PendingUpload[] = [];
    Array.from(files).forEach((f) => {
      if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
        toast.error(`${f.name}: only PDFs are accepted`);
        return;
      }
      if (f.size > 30 * 1024 * 1024) {
        toast.error(`${f.name}: max 30 MB per file`);
        return;
      }
      next.push({
        id: newId(),
        source: 'file',
        kind: kindFilter || 'exam_paper',
        file: f,
        display_name: f.name,
        size_bytes: f.size,
        progress: 0,
      });
    });
    if (!next.length) return;
    setPending((prev) => [...prev, ...next]);
    setSheetOpen(true);
  };

  const acceptUrl = (url: string) => {
    let host = url;
    try {
      const u = new URL(url);
      const tail = (u.pathname || '/').split('/').filter(Boolean).pop() || u.hostname;
      host = decodeURIComponent(tail);
    } catch { /* keep raw */ }
    setPending((prev) => [
      ...prev,
      {
        id: newId(),
        source: 'url',
        kind: kindFilter || 'exam_paper',
        url,
        display_name: host,
        progress: 0,
      },
    ]);
    setSheetOpen(true);
    toast.success('Link queued — tag it and hit upload to fetch on the server');
  };

  const patchPending = (id: string, patch: Partial<PendingUpload>) =>
    setPending((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const removePending = (id: string) =>
    setPending((prev) => prev.filter((p) => p.id !== id));

  const submitPending = async () => {
    // Curriculum uploads only need Speciality + Level — the CDACC unit
    // standard covers every course in that level, so Module/Course
    // don't apply and aren't collected by the picker in that mode.
    const ready = pending.filter((p) => {
      const isCurriculum = (p.kind || 'exam_paper') === 'curriculum';
      const taxonomyOk = isCurriculum
        ? p.speciality_key && p.level_key
        : p.speciality_key && p.level_key && p.module_key && p.course_id;
      return taxonomyOk && (p.source === 'file' ? !!p.file : !!p.url);
    });
    if (!ready.length) {
      toast.error(
        kindFilter === 'curriculum'
          ? 'Anchor every curriculum to a Speciality → Level first'
          : 'Anchor every paper to a Speciality → Level → Module → Course path first',
      );
      return;
    }
    // Shared taxonomy metadata builder — identical for file + URL flows.
    // Curriculum uploads skip year + paper_type (irrelevant) and default
    // paper_type server-side to 'Reference' so the taxonomy filter still
    // works for anyone browsing the corpus. They also skip module/course
    // — a curriculum grounds every course under its Speciality/Level.
    const buildMeta = (p: PendingUpload) => {
      const isCurriculum = (p.kind || 'exam_paper') === 'curriculum';
      return {
        kind:             p.kind || 'exam_paper',
        course_id:        isCurriculum ? undefined : p.course_id,
        course_code:      isCurriculum ? undefined : p.course_code,
        course_title:     isCurriculum ? undefined : p.course_title,
        speciality:       p.speciality_name,
        speciality_key:   p.speciality_key,
        level:            p.level_name,
        level_key:        p.level_key,
        module:           isCurriculum ? undefined : p.module_title,
        module_key:       isCurriculum ? undefined : p.module_key,
        unit:             isCurriculum ? undefined : p.course_title,
        year:             (isCurriculum ? undefined : p.year),
        paper_type:       (isCurriculum ? 'Reference' : (p.paper_type || 'Final')),
        academic_session: p.academic_session,
      };
    };

    for (const p of ready) {
      try {
        if (p.source === 'file' && p.file) {
          const fd = new FormData();
          fd.append('file', p.file);
          fd.append('meta', JSON.stringify(buildMeta(p)));
          await aiTrainerClient.post('/papers', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (ev) => {
              const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
              patchPending(p.id, { progress: pct });
            },
          });
        } else if (p.source === 'url' && p.url) {
          // Show a soft "fetching" indicator while the server pulls the PDF.
          patchPending(p.id, { progress: 50 });
          await aiTrainerClient.post('/papers/by-url', {
            url:  p.url,
            meta: buildMeta(p),
          });
          patchPending(p.id, { progress: 100 });
        }
        removePending(p.id);
      } catch (err: any) {
        const msg = err?.response?.data?.detail
          || err?.response?.data?.message
          || 'Upload failed';
        patchPending(p.id, { error: msg, progress: 0 });
      }
    }
    toast.success('Uploads queued — ingestion runs in the background');
    fetchPapers();
    fetchStats();
    if (!pending.some((p) => {
      const isCurriculum = (p.kind || 'exam_paper') === 'curriculum';
      return isCurriculum
        ? (!p.speciality_key || !p.level_key)
        : (!p.speciality_key || !p.level_key || !p.module_key || !p.course_id);
    })) setSheetOpen(false);
  };

  /* ── Library actions ────────────────────────────────────────── */
  const reprocess = async (id: string) => {
    try {
      await aiTrainerClient.post(`/papers/${id}/reprocess`);
      toast.success('Reprocess queued');
      fetchPapers();
    } catch { toast.error('Failed to reprocess'); }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Remove this paper from the training corpus?')) return;
    try {
      await aiTrainerClient.post(`/papers/${id}/delete`);
      toast.success('Removed');
      setDetail(null);
      fetchPapers();
      fetchStats();
    } catch { toast.error('Failed to remove'); }
  };

  const generateFrom = (id: string) => {
    // Generation UI ships in a follow-up release — for now we just
    // hint that the paper is ready to be drawn from.
    toast.message('Generation surface coming soon', {
      description: `Paper ${id.slice(0, 8)} is ready for the generator.`,
    });
  };

  /* ── Derived ────────────────────────────────────────────────── */
  const filtered = useMemo<Paper[]>(() => {
    const q = search.trim().toLowerCase();
    return papers
      .filter((p) => {
        if (!kindFilter) return true;
        // Legacy rows may not carry `kind` yet — treat missing as 'exam_paper'.
        return (p as any).kind === kindFilter
          || (kindFilter === 'exam_paper' && !(p as any).kind);
      })
      .filter((p) => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'ready') return p.status === 'ready';
        if (statusFilter === 'failed') return p.status === 'failed';
        return PROCESSING.includes(p.status); // 'processing'
      })
      .filter((p) => {
        if (!q) return true;
        return `${p.file_name} ${p.course_code ?? ''} ${p.course_title ?? ''} ${p.year ?? ''}`
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => (b.uploaded_at || '').localeCompare(a.uploaded_at || ''));
  }, [papers, search, statusFilter, kindFilter]);

  const processingCount = papers.filter((p) => PROCESSING.includes(p.status)).length;

  return (
    <div
      className="min-h-screen min-w-0 overflow-x-clip bg-slate-50 px-3 pb-20 pt-3 text-slate-900 sm:px-5 sm:pt-5 lg:px-6 lg:pt-6"
      style={{ fontFamily: "'Afacad Flux', system-ui, sans-serif" }}
    >
      {/* Identity + sign-out live in the sidebar now — this page just
          leads with the brand hero. */}

      <CorpusHero
        processingCount={processingCount}
        loading={loading}
        onRefresh={() => { fetchPapers(); fetchStats(); }}
        // Upload button now toggles the right-side sheet — the drop
        // zone and the URL entry both live inside it.
        onUpload={() => setSheetOpen((open) => !open)}
        eyebrow={heroEyebrow}
        title={heroTitle}
        sub={heroSub}
        ctaLabel={kindFilter === 'curriculum' ? 'Upload curriculum' : undefined}
        variant={kindFilter === 'curriculum' ? 'curriculum' : 'papers'}
      />

      <KPIStrip stats={stats} />

      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
          <Search size={13} className="flex-shrink-0 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by file, course or year…"
            className="min-w-0 flex-1 border-none bg-transparent text-[13.5px] text-slate-900 outline-none"
          />
        </label>
        <div className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
          <Filter size={12} className="mx-1.5 text-slate-400" />
          {(['all', 'ready', 'processing', 'failed'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`
                rounded-lg px-3 py-1.5 text-[12px] font-bold capitalize transition
                ${statusFilter === s
                  ? 'bg-[#1a5276] text-white'
                  : 'bg-transparent text-slate-600 hover:bg-slate-50'}
              `}
            >
              {s === 'all' ? 'All' : s}
            </button>
          ))}
        </div>
      </div>

      {/* Library */}
      {loading ? (
        <EmptyState icon={<Activity size={20} />} text="Loading corpus…" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Sparkles size={20} className="text-[#0e9172]" />}
          text={papers.length
            ? 'No papers match your filter.'
            : 'Your corpus is empty — upload your first past paper to get started.'}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <PaperCard
              key={p.id}
              paper={p}
              onOpen={() => setDetail(p)}
              onReprocess={() => reprocess(p.id)}
              onPreview={() => setPreview(p)}
            />
          ))}
        </div>
      )}

      {/* Slide-up: pending uploads */}
      <PendingSheet
        open={sheetOpen}
        pending={pending}
        taxonomy={taxonomy}
        taxonomyLoading={taxonomyLoading}
        onClose={() => setSheetOpen(false)}
        onClear={() => { setPending([]); setSheetOpen(false); }}
        onSubmit={submitPending}
        onRemove={removePending}
        onPatch={patchPending}
        onAddUrl={acceptUrl}
        onAddFiles={acceptFiles}
        lockedKind={kindFilter}
      />

      {/* Side drawer: per-paper detail (slides from right). */}
      <DetailDrawer
        paper={detail}
        onClose={() => setDetail(null)}
        onReprocess={reprocess}
        onRemove={remove}
        onGenerate={generateFrom}
        onPreview={() => detail && setPreview(detail)}
      />

      {/* PDF preview drawer (slides from left). Sits on a higher
          z-index than the detail drawer so a user can open the
          preview while the detail drawer is still visible. */}
      <PdfPreviewDrawer
        paper={previewOf}
        onClose={() => setPreview(null)}
      />
    </div>
  );
};

const EmptyState: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-8 text-center text-[13px] text-slate-500">
    {icon}
    <p className="m-0">{text}</p>
  </div>
);

export default ExamCorpusUpload;
